/**
 * IL BOTTONE UNICO DELL'INCONTRO — quale faccia mostra, dato lo stato che il server ha detto.
 *
 * 🔴 **La decisione** (Luca, 2026-09-25T13:14:47Z, L-2196, testuale: «sostituisce»): dentro la
 * scheda d'incontro un solo bottone d'azione, in fondo, che cambia faccia seguendo lo stato —
 * «Sono pronto» → l'attesa dell'avversario → «Apri Clash Royale» quando i due check-in ci sono e
 * l'invito e' partito → il referto. Sostituisce le righe del pannello azioni; «Ritirati» passa nel
 * menu ⋯ della scheda. Nasce dal collaudo-335acfb4 (25/09 12:35-12:50Z): il check-in d'incontro
 * c'era, e non si trovava. Proposta e immagini: KB `services/qlash-telegram-app/ui/
 * 2026-09-25-azione-persistente-proposta.md`.
 *
 * ⭐ **Questa funzione NON ridefinisce nessuna regola: ordina quelle che esistono.** Ogni ramo legge
 * un verdetto gia' calcolato altrove — `matchActionsOf` (il permesso del server, i blocchi),
 * `matchCheckInPlanOf` (la finestra), `proposalRoleOf` (chi conferma), `crInviteOf` (l'invito),
 * `openGameTargetOf` (se il gioco si apre). Qui si decide solo QUALE di quei gesti e' il prossimo.
 *
 * ⛔ **Nessun ramo guarda l'orologio** (ADR-0158, e la regola di `matchCheckInPlanOf`): la faccia
 * cambia quando cambia il server. Il conto alla rovescia e' testo, e lo scrive il componente.
 *
 * 🔴 **D2 CAMBIA, ed e' la conseguenza di «sostituisce»** — dichiarata, non taciuta (revisione di
 * tma-orc su #384, punto 7). Prima ogni azione aveva la sua riga, e ciascuna spenta diceva il SUO
 * perche'. Col bottone unico si vede UN gesto: il prossimo. ⇒ il motivo che resta a schermo e'
 * quello del gesto che il bottone rappresenta (faccia `motivo`, o i due bottoni di check-in spenti
 * col loro motivo); i motivi delle azioni NON scelte non ci sono piu'. Quando nessun gesto ha senso
 * (concluso, bye, da comporre, depositato) il bottone non c'e' (`null`), come gia' il pannello.
 * La regola di Luca del 06/09 («quando non si puo' premere, lo dice») vale per il gesto mostrato.
 *
 * ⚠️ Pura e senza `import.meta.env`: la esercita `tools/verify-limulo.ts` sotto Node.
 */
import type { LimuloCrInvite } from './externalData.ts';
import type { LimuloMatchState, LimuloProposalRole } from './match.ts';
import {
  actionPresentationOf,
  type LimuloActionAvailability,
  type LimuloActionBlock,
  type LimuloMatchAction,
  type LimuloMatchCheckInPlan,
} from './matchActions.ts';
import type { LimuloOpenGameBlock } from './openGame.ts';

/**
 * I blocchi che fermano anche il GIOCO, non solo il gesto: il torneo sta chiudendo il check-in,
 * decide lo staff, oppure si aspetta ancora un check-in. Con questi «Apri Clash Royale» non si offre.
 * 🔴 E il torneo che non e' in corso (kb#303 §3, scelta di tma-orcr 28/09 01:12Z): li' il motore rifiuta le
 * scritture del giocatore (check-in, referto, ritiro). Senza questa voce, a permessi spenti la faccia
 * diventava «Apri Clash Royale» [M banco, 28/09].
 * ⚠️ Ferma SOLO il ripiego «Apri …» senza invito (punto 8 qui sotto). Un invito CR vivo e il tavolo QGammon
 * (punto 5) vengono PRIMA e restano: in `provider` il risultato del gioco entra anche in pausa (il motore
 * ferma solo la fonte `player`), e il tavolo ha il suo orologio. In `manual` il referto dopo la partita
 * prenderebbe il 409 — e' una decisione aperta, non una svista (revisione 1 di #474, nota al punto 4).
 */
const BLOCCHI_CHE_FERMANO_IL_GIOCO: ReadonlySet<LimuloActionBlock> = new Set([
  'checkin-closing',
  // La stessa finestra di `checkin-closing`, col torneo in pausa (qTournaments 1.84.0).
  'checkin-in-pausa',
  'staff-decides',
  'awaiting-check-in',
  'torneo-non-in-corso',
  'torneo-terminato',
]);

/** Le facce. Le prime quattro si premono; `attesa` e `motivo` no, e dicono perche'. */
export type LimuloFacciaBottone =
  /**
   * Il check-in: «Sono pronto» a finestra aperta (col «Non sono pronto» sotto), «Check-in» senza
   * finestra. `blocco` non nullo = a finestra APERTA il server lo nega: i due bottoni restano,
   * SPENTI col motivo (D2) — c'e' una finestra aperta, e chi la cerca deve trovarla.
   */
  | {
      kind: 'checkin';
      etichetta: 'pronto' | 'checkin';
      nonPronto: boolean;
      blocco: LimuloActionBlock | null;
      /** Il referto e' concesso: resta raggiungibile sotto (revisione #384, punto 2). */
      referto: boolean;
    }
  /** «Confermo» il referto dell'altro lato; passati i 15 minuti «Chiudi con questo referto». Sotto, «Non e' andata cosi'». */
  | { kind: 'conferma'; scaduto: boolean }
  /**
   * «Apri Clash Royale». `invito`: il torneo ha creato l'invito e lo si deve accettare adesso
   * (solo quando il referto NON e' concesso: v. il ramo). Senza invito: nient'altro da fare, si gioca.
   */
  | { kind: 'apri-gioco'; invito: boolean }
  /**
   * «Entra al tavolo» (I-189): il tavolo QGammon di un incontro backgammon e' aperto e aspetta il
   * giocatore. E' l'analogo dell'invito vivo, ma in-app: porta a `/backgammon/match/:id`.
   */
  | { kind: 'tavolo' }
  /**
   * «Invia referto». Sotto, «Apri Clash Royale» se il gioco si apre; `invito` = c'e' un invito vivo,
   * e sotto si dice il tag (senza «accettalo subito»: non sappiamo se la partita e' gia' giocata).
   */
  | { kind: 'referto'; apriGioco: boolean; invito: boolean }
  /** Sono pronto, e si aspetta l'avversario: non si preme. */
  | { kind: 'attesa' }
  /** Niente da premere adesso, e il motivo. */
  | {
      kind: 'motivo';
      motivo:
        | { da: 'azione'; block: LimuloActionBlock }
        | { da: 'gioco'; block: LimuloOpenGameBlock }
        | { da: 'partita-morta' }
        | { da: 'disputa' };
    };

export interface LimuloFacciaInput {
  /** `matchActionsOf(match, { checkinDone })`. */
  azioni: Record<LimuloMatchAction, LimuloActionAvailability>;
  /** `matchCheckInPlanOf(match)`. */
  piano: LimuloMatchCheckInPlan;
  /** `matchStateOf(match)`. */
  stato: LimuloMatchState;
  /** `proposalRoleOf(match)`. */
  ruolo: LimuloProposalRole;
  /** C'e' un punteggio proposto da confermare (una serie a gare non si conferma da qui). */
  punteggioProposto: boolean;
  /** I 15 minuti dell'assenso sono passati (v. `assensoScaduto` nel componente). */
  assensoScaduto: boolean;
  /** `crInviteOf(...)`, gia' calcolato solo su un incontro giocabile. */
  invito: LimuloCrInvite | null;
  /** `externalMatchDeadOf(match.externalMatch)` fuori da un incontro non giocabile. */
  partitaMorta: boolean;
  /** `openGameTargetOf(...)`: se il gioco si apre, e se no perche'. */
  apriGioco: { available: boolean; block: LimuloOpenGameBlock | null };
  /**
   * `tavoloDaAprireOf(...) !== null`: c'e' un tavolo QGammon in cui entrare (I-189). Facoltativo:
   * assente = nessun tavolo, cioe' il comportamento di prima per ogni gioco che non e' backgammon.
   */
  tavolo?: boolean;
  /**
   * `tavoloChiusoOf(...)`: le istruzioni parlano di un tavolo QGammon che per chi guarda e' chiuso —
   * qui conta il caso in cui la sessione ha visto la partita FINIRE e l'incontro e' ancora `pending`
   * (~45 s, #403). Facoltativo: assente = come prima.
   */
  tavoloFinito?: boolean;
}

/**
 * Il prossimo gesto, in ordine di vita dell'incontro: check-in → gioco → referto.
 *
 * L'ordine dei rami E' la logica, non stile, e un principio lo tiene insieme: **un permesso che il
 * server concede non viene mai coperto da una nostra lettura** (revisione di tma-orc su #384, punti
 * 2, 3, 5, 6). Ogni ramo che «ferma» — l'attesa, la partita morta, la disputa, i blocchi — scatta
 * solo quando il gesto concesso non c'e'.
 *  1. gli stati senza gesto (bye, da comporre, depositato, concluso): niente bottone;
 *  2. a finestra APERTA il check-in — acceso, o spento col motivo SOLO se il referto non e' concesso;
 *     anche durante una disputa: il server lo concede, e `matchActions.ts` lo tiene apposta. Se il
 *     check-in l'ha appena registrato QUESTA scheda (`checkin-done`), il motivo e basta, senza i due
 *     bottoni spenti (#426) — sempre solo se il referto non e' concesso;
 *  3. l'attesa dell'avversario, solo se il referto non e' concesso;
 *  4. la conferma del referto dell'altro lato;
 *  5. l'invito vivo — incontro PENDENTE e referto NON concesso (cioe' in `provider`): li' «Apri Clash
 *     Royale» e' l'unico gesto. Subito dopo, alle stesse condizioni, il tavolo QGammon (I-189). Col referto concesso (manuale) non sappiamo se la partita e' gia'
 *     giocata — il motore non riscrive `opened` e scrive solo tre stati esterni, tutti di morte — e
 *     vince il referto, con l'invito sotto;
 *     5-bis. il tavolo QGammon che questa sessione ha visto FINIRE, a incontro ancora pendente (#403,
 *     ~45 s): nessun gesto, e il motivo e' quello che il server ha gia' dato al referto
 *     (`provider-result`) — SOLO se e' quello. In `manual`, o col modo vuoto, si prosegue;
 *  6. il check-in senza finestra, col referto sotto se concesso;
 *  7. il referto;
 *  8. nessun gesto concesso: partita morta, disputa, i blocchi che fermano il gioco;
 *  9. il gioco da aprire senza invito;
 *     9-bis. Tennis Clash dove il gioco si aprirebbe ma non da qui (`not-a-player`, `phone-only`, `no-link`):
 *     il motivo, DOPO i blocchi del punto 8 e PRIMA delle azioni spente (revisione di #517, punti 5-6);
 *     poi un'azione spenta col suo motivo, oppure niente.
 */
export function facciaBottoneOf(input: LimuloFacciaInput): LimuloFacciaBottone | null {
  const { azioni, piano, stato, ruolo, invito, apriGioco } = input;
  const referto = azioni.result.available;

  // Gli stati in cui la scheda non monta nemmeno il pannello (`matchActionsPanelVisible`): nessuna
  // azione bloccata da spiegare, niente da fare. Stessa enumerazione, detta qui perche' la funzione
  // non dipenda da chi la chiama.
  if (stato === 'bye' || stato === 'incomplete' || stato === 'reported' || stato === 'decided') return null;

  if (piano.kind === 'aperto') {
    if (azioni.checkin.available) {
      return { kind: 'checkin', etichetta: 'pronto', nonPronto: true, blocco: null, referto };
    }
    // `checkin-done` non e' il server che nega a finestra aperta (D2): e' la NOSTRA richiesta appena
    // andata a buon fine (`checkinDone`, solo su `submitted`), e il piano resta `aperto` finche' una
    // rilettura non porta `myCheckin: true` (allora il piano e' `pronto`). Coi due bottoni spenti la
    // scheda rimetteva a schermo «Sono pronto» e «Non sono pronto» subito dopo il tocco [M tma-orcb
    // 26/09 09:2xZ, ~100 ms, sui due profili]. ⇒ Il motivo e basta: il posto del bottone resta,
    // spento, con la frase (decisione 1 di Luca del 06/09 in `LimuloMatchActions.tsx`).
    // ⚠️ SOLO qui, a finestra aperta. Senza finestra o a finestra chiusa `checkin-done` cade fra le
    // azioni spente in fondo (punto 9), DOPO l'invito, il tavolo e il gioco da aprire (il gate lo fissa, #426).
    if (azioni.checkin.block === 'checkin-done' && !referto) {
      return { kind: 'motivo', motivo: { da: 'azione', block: 'checkin-done' } };
    }
    if (actionPresentationOf(azioni.checkin) === 'spento' && !referto) {
      return { kind: 'checkin', etichetta: 'pronto', nonPronto: true, blocco: azioni.checkin.block, referto: false };
    }
  }
  if (piano.kind === 'pronto' && piano.readyAt === null && !referto) return { kind: 'attesa' };

  if (ruolo === 'confirmer' && input.punteggioProposto) {
    return { kind: 'conferma', scaduto: input.assensoScaduto };
  }

  // ⚠️ `!partitaMorta` anche se `crInviteOf` gia' marca scaduto l'invito di una partita morta: la
  // funzione non si fida della coerenza di chi la chiama.
  const invitoVivo =
    apriGioco.available && invito !== null && !invito.expired && !input.partitaMorta && stato === 'pending';
  if (invitoVivo && !referto) return { kind: 'apri-gioco', invito: true };
  // 🔴 Il tavolo QGammon aperto (I-189) — stesso posto e stesse condizioni dell'invito vivo: incontro
  // PENDENTE, referto NON concesso (in `provider` il risultato lo porta il gioco), partita non morta.
  // Prima di qui il backgammon cadeva in fondo, su «per questo gioco non conosciamo un modo di aprirlo»
  // — sotto un riquadro che diceva «Entra al tavolo» (reperto di Luca su stg-tma, 2026-09-25).
  // ⚠️ Col referto concesso vince il referto, come per l'invito: il tavolo resta nel riquadro sopra.
  if (input.tavolo === true && stato === 'pending' && !input.partitaMorta && !referto) return { kind: 'tavolo' };
  // 🔴 E a partita FINITA, finche' il motore non chiude l'incontro: il tavolo non c'e' piu' e il gesto
  // non esiste. Senza questo ramo la scheda cadeva piu' in fondo, sul check-in gia' registrato o su «per
  // questo gioco non conosciamo un modo di aprirlo».
  // ⚠️ Il motivo NON si inventa (revisione indipendente di #408, punto 2): si mostra solo se e' il
  // verdetto che `matchActionsOf` ha gia' dato al referto — `provider-result`, cioe' `resultMode ===
  // 'provider'`. In `manual` (il ripiego d'emergenza del motore su un torneo `provider` conserva la
  // partita aperta) o col modo vuoto, «il risultato lo manda il gioco» sarebbe falso: si prosegue, e
  // vince il referto concesso o il blocco vero del server. `block === 'provider-result'` implica gia'
  // che il referto non e' concesso.
  if (
    input.tavoloFinito === true &&
    stato === 'pending' &&
    !input.partitaMorta &&
    azioni.result.block === 'provider-result'
  ) {
    return { kind: 'motivo', motivo: { da: 'azione', block: 'provider-result' } };
  }

  // 🔴 SENZA finestra il check-in viene DOPO l'invito vivo: su quel piano la facciata puo' rispondere
  // `canCheckIn: true` anche a check-in fatto quando `myCheckin` non arriva (v. `checkin-done`), e il
  // «Check-in» coprirebbe l'invito. ⚠️ Sul servito `myCheckin` e' booleano per chi ha una parte
  // nell'incontro, con o senza finestra [M `qlash-limulo@origin/staging` `4ef5369`:
  // `incontri.js:492-498` lo calcola da `mieParti` senza guardare `matchCheckIn`; `policy.js:612` lo
  // porta], e un secondo check-in e' un 200 idempotente (`incontri.js:2396-2398`). Il referto concesso
  // resta comunque raggiungibile, sotto.
  if (piano.kind === 'invariato' && azioni.checkin.available) {
    return { kind: 'checkin', etichetta: 'checkin', nonPronto: false, blocco: null, referto };
  }
  if (referto) return { kind: 'referto', apriGioco: apriGioco.available, invito: invitoVivo };

  // Da qui nessun gesto e' concesso dal server.
  if (input.partitaMorta) return { kind: 'motivo', motivo: { da: 'partita-morta' } };
  if (stato === 'disputed') return { kind: 'motivo', motivo: { da: 'disputa' } };

  // Un'azione che esisterebbe, spenta. L'ordine segue la vita dell'incontro: finche' la finestra
  // non e' chiusa e non c'e' un referto conta il check-in («Check-in gia' registrato»); dopo, il
  // referto — ed e' li' che finiscono la chiusura in corso (`checkin-closing`), la doppia assenza
  // (`staff-decides`) e l'attesa della conferma di chi ha gia' refertato (`awaiting-confirmation`).
  const ordine =
    piano.kind === 'chiuso' || stato !== 'pending' ? (['result', 'checkin'] as const) : (['checkin', 'result'] as const);
  const spenta = (fermano: boolean): LimuloFacciaBottone | null => {
    for (const action of ordine) {
      const a = azioni[action];
      if (actionPresentationOf(a) !== 'spento' || a.block === null) continue;
      if (fermano && !BLOCCHI_CHE_FERMANO_IL_GIOCO.has(a.block)) continue;
      return { kind: 'motivo', motivo: { da: 'azione', block: a.block } };
    }
    return null;
  };
  // 🔴 Prima i blocchi che dicono «non si gioca adesso»: col check-in che si sta chiudendo, o con
  // la doppia assenza lasciata allo staff, «Apri Clash Royale» manderebbe a giocare un incontro che
  // il torneo sta decidendo senza partita.
  const ferma = spenta(true);
  if (ferma !== null) return ferma;
  if (apriGioco.available && stato === 'pending') return { kind: 'apri-gioco', invito: false };
  // 🔴 Tennis Clash, dove il gioco SI APRIREBBE ma non da qui: `not-a-player` (chi guarda non e' uno dei due
  // lati), `phone-only` (desktop, web, piattaforma sconosciuta) e `no-link` (per questo telefono il provider
  // non ha dato il link). Il motivo passa PRIMA delle azioni spente: dopo il check-in `spenta(false)`
  // risponderebbe «check-in gia' registrato» e chi e' su un desktop non leggerebbe mai che la partita si apre
  // dal telefono [M banco, 30/09]. ⚠️ Ma DOPO `spenta(true)` qui sopra: un blocco che dice «non si gioca adesso»
  // (check-in che si chiude, staff che decide, torneo non in corso) e' piu' forte di «si apre dal telefono»
  // (`tests/tennis-clash-apri-partita.test.tsx`, revisione di #517 punto 5). ⚠️ Solo questi tre blocchi, che
  // nascono solo da Tennis Clash a flag acceso: per ogni altro gioco l'ordine e' quello di prima.
  if (
    stato === 'pending' &&
    (apriGioco.block === 'phone-only' || apriGioco.block === 'no-link' || apriGioco.block === 'not-a-player')
  ) {
    return { kind: 'motivo', motivo: { da: 'gioco', block: apriGioco.block } };
  }
  const altra = spenta(false);
  if (altra !== null) return altra;
  if (stato === 'pending' && apriGioco.block !== null && apriGioco.block !== 'not-playable') {
    return { kind: 'motivo', motivo: { da: 'gioco', block: apriGioco.block } };
  }
  return null;
}
